import { MarketDataCardState, type MarketDataCardConfig } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import type { MarketSelectorItem } from '../MarketSelector/types';
import { isMarketSelectorItemSelectable } from '../MarketSelector/utils/items';
import { getOneAccountPerMarket } from '../OMS/utils';

export type MarketDataCardConfigAction =
  | {
      type: 'save';
      config: MarketDataCardConfig;
    }
  | {
      type: 'remove';
    }
  | undefined;

interface GetMarketDataCardConfigActionParams {
  config: MarketDataCardConfig;
  items: MarketSelectorItem[];
  hasSecurity: boolean;
}

/**
 * Determines whether a market card with `initializeMarketAccounts` set should be updated or removed.
 *
 * When initialization is applicable, this will either:
 * - return a `save` action containing a config with one account per market and all standalone markets selected, or
 * - return a `remove` action if the symbol has no selectable markets/accounts.
 *
 * Returns `undefined` when no initialization work is needed.
 */
export function getMarketDataCardConfigAction({
  config,
  items,
  hasSecurity,
}: GetMarketDataCardConfigActionParams): MarketDataCardConfigAction {
  const shouldInitializeMarketAccounts =
    config.initializeMarketAccounts === true &&
    config.state !== MarketDataCardState.NEW_CARD &&
    config.symbol != null &&
    hasSecurity;
  if (!shouldInitializeMarketAccounts) {
    return;
  }

  const initialSelections = getInitialSelections(items);
  const hasNoSelections =
    initialSelections.selectedMarkets.length === 0 && initialSelections.selectedMarketAccounts.length === 0;
  if (hasNoSelections) {
    return { type: 'remove' };
  }

  const newConfig: MarketDataCardConfig = {
    ...config,
    ...initialSelections,
    initializeMarketAccounts: false,
  };

  return isEqual(config, newConfig) ? undefined : { type: 'save', config: newConfig };
}

function getInitialSelections(items: MarketSelectorItem[]) {
  const selectableItems = items.filter(isMarketSelectorItemSelectable);
  const selectedMarkets = selectableItems
    .filter((item): item is Extract<MarketSelectorItem, { type: 'Market' }> => item.type === 'Market')
    .map(item => item.name);
  const selectedMarketAccounts = getOneAccountPerMarket(
    selectableItems
      .filter((item): item is Extract<MarketSelectorItem, { type: 'MarketAccount' }> => item.type === 'MarketAccount')
      .map(item => item.marketAccount)
  ).map(marketAccount => marketAccount.Name);

  return {
    availableMarkets: selectedMarkets,
    selectedMarkets,
    availableMarketAccounts: selectedMarketAccounts,
    selectedMarketAccounts,
  };
}
